/**
 * fetchApi通信のショートハンド
 */
import format from 'date-fns/format'
import api from 'config/api'
import type {
  MedicalInstitutionProps,
  ReservedReservationProps,
  ReservationFormProps,
  StripeSettingProps,
  PasscodeEntryApiProps,
  ReceptionEmailSendProps,
  ReceptionSmsSendProps,
  EligibilityVerificationAppIdParams,
} from 'types/applicant'
import {
  BankTransfer,
  Billing,
  DepartmentSettingsProps,
  PrescriptionSetting,
  ReservationFrameProps,
  SaluDiUserAccessCodeParams,
} from 'types/med'
import {
  PharmacySearchApiParams,
  EligibilityVerificationUrlParams,
} from 'types/common'
import type { CallMethodNames, ImageProps, PharmacyProps } from 'types/common'
import { DateString } from 'config/format'
import { PreReservation } from 'types/med'
import { client, fetchWithTimeout } from 'app/client'

export const getReservation = ({
  access_key,
  pass_code,
}: {
  access_key: string
  pass_code?: string
}) =>
  client<ReservedReservationProps>(`${api.reservations}/${access_key}`, {
    pass_code,
  })

export const getReservationIds = (access_key: string) =>
  client<PasscodeEntryApiProps>(`${api.reservationIds}/${access_key}`)

export const getDepartmentSettings = ({
  departmentId,
  access_key,
}: {
  departmentId: number
  access_key: string
}) =>
  client<DepartmentSettingsProps>(`${api.departmentSettings}/${departmentId}`, {
    access_key,
  })

export const getReservationFrames = ({
  departmentId,
  access_key,
  date,
}: {
  departmentId: number
  access_key: string
  date: Date
}) =>
  client<ReservationFrameProps[]>(`${api.reservationFrames}/${departmentId}`, {
    access_key,
    reservation_date: format(date, DateString),
  })

export const postReservation = (data: ReservationFormProps) =>
  client<ReservationFormProps>(api.reservations, data, 'POST', fetchWithTimeout)

export const putReservation = ({
  data,
  access_key,
}: {
  data: ReservationFormProps
  access_key: string
}) =>
  client<ReservationFormProps>(
    `${api.reservations}/${access_key}`,
    data,
    'PUT',
    fetchWithTimeout
  )

export type AttachmentImageNameProps =
  | 'attachment_images'
  | 'identity_verification_images'
  | 'insurance_card_images'
  | 'certificate_images'

export const getImagesPromise = async ({
  reservation_id,
  pass_code,
  name,
  access_key,
}: {
  name: AttachmentImageNameProps
  access_key: string
  pass_code?: string // NOTE: 申込者
  reservation_id?: number // NOTE: 医療機関ログイン時
}) => {
  let path: string
  switch (name) {
    case 'attachment_images':
      path = api.reservationAttachmentImages
      break
    case 'identity_verification_images':
      path = api.reservationIdentityVerificationImages
      break
    case 'insurance_card_images':
      path = api.reservationInsuranceCardImages
      break
    case 'certificate_images':
      path = api.reservationCertificateImages
      break
    default:
      path = api.reservationAttachmentImages
      break
  }
  let url = `${path}/${access_key}`
  if (reservation_id) {
    url = `${path}/${reservation_id}`
  }

  return client<ImageProps[]>(url, {
    pass_code,
  })
}

export const getAllImages = ({
  reservation_id,
  pass_code,
  names,
  access_key,
}: {
  reservation_id?: number
  pass_code?: string
  names: AttachmentImageNameProps[]
  access_key: string
}) => {
  return Promise.all<ImageProps[]>(
    names.map((name) =>
      getImagesPromise({
        reservation_id,
        pass_code,
        name,
        access_key,
      })
    )
  )
}

export const getMed = (access_key: string) =>
  client<MedicalInstitutionProps>(
    `${api.reservationMedicalInstitutions}/${access_key}`
  )

export const getVcUrl = (app: CallMethodNames) => (access_key: string) => {
  let url = null
  switch (app) {
    case 'video_zoom':
      url = api.videoMeetings
      break
    case 'video_teams':
      url = api.teamsMeetings
      break
  }
  return client<{ url: string }>(`${url}/${access_key}`)
}

export const putReservationCancel = ({
  access_key,
  pass_code,
}: {
  access_key: string
  pass_code?: string
}) =>
  client<void>(`${api.reservationCancel}/${access_key}`, { pass_code }, 'PUT')

export const getPreReservation = (
  provisional_reservation_access_key: PreReservation['provisional_reservation_access_key']
) =>
  client<PreReservation>(
    `${api.preReservations}/${provisional_reservation_access_key}`
  )

export const postVerification = (
  params: ReceptionEmailSendProps | ReceptionSmsSendProps
) => {
  let url =
    (params as ReceptionEmailSendProps).email !== undefined
      ? api.verificationEmail
      : api.verificationSms
  return client<void>(url, params, 'POST')
}

export const getBilling = (access_key: string) =>
  client<Billing[]>(`${api.billings}/${access_key}`)

export const getBank = (access_key: string) =>
  client<BankTransfer>(api.bankTransferSettings, {
    access_key,
  })

export const getGoogleMapsApiKey = (access_key: string) =>
  client<{ api_key: string }>(api.googleMapsApiKey, {
    access_key,
  })

export const getNearPharmacies = (access_key: string) =>
  client<PharmacyProps[]>(api.nearPharmacies, {
    access_key,
  })

export const getPrescription = (access_key: string) =>
  client<PrescriptionSetting>(api.prescriptionMedicines, {
    access_key,
  })

export const searchPharmacies = (
  access_key: string,
  params: PharmacySearchApiParams
) =>
  client<PharmacyProps[]>(api.pharmacies, {
    access_key,
    ...params,
  })

export const getStripeKeys = (access_key: string) =>
  client<StripeSettingProps>(`${api.stripeConnects}/${access_key}`)

export const getReservableDates = (
  department_id: number,
  params: {
    access_key: string
    from_date: string
    to_date: string
  }
) =>
  client<{ reservable_date: string }[]>(
    `${api.reservableDates}/${department_id}`,
    params
  )

/**
 * オンライン資格確認
 */
export const getEligibilityVerificationAppId = (access_key: string) =>
  client<EligibilityVerificationAppIdParams>(
    api.onlineEligibilityVerificationAppId,
    {
      access_key,
    }
  )
export const getEligibilityVerificationUrl = (access_key: string) =>
  client<EligibilityVerificationUrlParams>(
    api.onlineEligibilityVerificationUrl,
    {
      access_key,
    }
  )

/**
 * SaluDiユーザーアクセスコードを取得
 */
export const getSaluGiUserAccessCode = (reservation_id: number) =>
  client<SaluDiUserAccessCodeParams>(
    `${api.reservations}/${reservation_id}/saludi-user-access-code`
  )
